import { HoverText, Icon } from '@advanza/ui'
import clsx from 'clsx'
import { format } from 'date-fns'
import React from 'react'

interface StatusColors {
    icon?: string
    bgColor?: string
    color?: string
}

// Also used for the status badge in the invoice list rows.
export const statusMap: Record<string, StatusColors> = {
    pending: { icon: 'accessible_forward', bgColor: '#ffed90' },
    open: { icon: 'schedule', bgColor: '#e4ffff' },
    queued: { icon: 'schedule', bgColor: '#e4ffff' },
    scheduled: { icon: 'schedule', bgColor: '#e4edff' },
    rescheduled: { icon: 'schedule', bgColor: '#e4edff' },
    fulfilled: { icon: 'done_all', bgColor: '#0ea66b', color: '#fff' },
    paid: { icon: 'done_all', bgColor: '#0ea66b', color: '#fff' },
    charged_back: { icon: 'sync_problem', bgColor: '#ff002f', color: '#fff' },
    failed: { icon: 'warning', bgColor: '#ff002f', color: '#fff' },
    payment_reminder_scheduled: { icon: 'notification_important', bgColor: '#fff1c5' },
    payment_reminder_sent: { icon: 'notification_important', bgColor: '#fff1c5' },
    payment_reminder_overdue: { icon: 'hourglass_bottom', bgColor: '#ffc5ca' },
    debt_collection: { icon: 'gavel', bgColor: '#262626', color: '#fff' },
    marked_lost: { icon: 'heart_broken', bgColor: '#5900b7', color: '#fff' },
    sepa_created: { icon: 'soap', bgColor: '#537ddf', color: '#fff' },
    payment_plan: { icon: 'calendar_view_month', bgColor: '#e3f2fd', color: '#1565c0' },
}

// Mirrors `InvoicesLogsTable::EVENT_*` on the backend.
const INVOICE_LOG_EVENTS = {
    statusUpdate: 'transaction_status_update',
    markedLost: 'marked_lost',
    debtCollection: 'debt_collection',
    partialPayment: 'partial_payment',
    molliePaymentEvent: 'mollie_payment_event',
} as const

// partial_payment is an invoices_log event, not a transaction_status, so it has
// its own colors instead of an entry in statusMap.
const partialPaymentColors: StatusColors = { bgColor: '#0e94a6', color: '#ffffff' }

// Mirrors `ManualFulfilmentMollieService::KIND_*`: a Mollie event on a manually
// fulfilled invoice, recorded without changing its status. One "needs a check"
// color for every kind, so an extra collected debit does not read as a green success.
const molliePaymentEventColors: StatusColors = { bgColor: '#fff1c5', color: '#7a5a00' }
const molliePaymentEventLabels: Record<string, string> = {
    paid: 'extra debit collected',
    charged_back: 'chargeback',
    refunded: 'refund',
    failed: 'debit failed',
}

interface InvoiceLogContent {
    trigger?: string
    from_status?: string | null
    to_status?: string | null
    undo?: boolean
    amount?: number
    open_amount?: number
    kind?: string
    payment_id?: string
    bank_reason_code?: string | null
    bank_reason?: string | null
}

interface InvoiceLog {
    invoices_log_id: number
    user_id: number | null
    event_name: string
    created: string | null
    content?: InvoiceLogContent | null
    user?: { id: number; username: string } | null
}

interface MollieLog {
    log_id: number
    message: string
    created?: string | null
}

export interface InvoiceWithLogs {
    invoice_nr?: string | number
    invoices_logs?: InvoiceLog[]
    mollie_logs?: MollieLog[]
}

const formatEuro = (value: number | undefined) => `€${Number(value).toFixed(2)}`

const invoiceLogActor = (log: InvoiceLog) => {
    if (log.user?.username) {
        return log.user.username
    }
    return log.content?.trigger || 'system'
}

const StatusPill = ({
    status,
    muted,
    colors: colorsOverride,
}: {
    status?: string | null
    muted?: boolean
    colors?: StatusColors
}) => {
    const colors = colorsOverride || (!muted && status && statusMap[status]) || {}
    return (
        <span
            className="inline-block whitespace-nowrap rounded-[10px] px-2 py-[2px] text-[11px] font-semibold leading-[14px]"
            style={{
                backgroundColor: colors.bgColor || '#f1f1f1',
                color: colors.color || '#555555',
                border: colors.bgColor ? 'none' : '1px solid #e0e0e0',
            }}>
            {status || 'none'}
        </span>
    )
}

// Rail dot takes the color of the resulting status so the trail is scannable
// without reading: green = ended up paid, red = charged back, etc.
const invoiceLogDotColor = (log: InvoiceLog) => {
    const content = log.content || {}
    if (content.undo) {
        return '#c8c8c8'
    }
    if (log.event_name === INVOICE_LOG_EVENTS.statusUpdate) {
        return (content.to_status && statusMap[content.to_status]?.bgColor) || '#c8c8c8'
    }
    if (log.event_name === INVOICE_LOG_EVENTS.partialPayment) {
        return partialPaymentColors.bgColor
    }
    if (log.event_name === INVOICE_LOG_EVENTS.molliePaymentEvent) {
        return molliePaymentEventColors.bgColor
    }
    return statusMap[log.event_name]?.bgColor || '#c8c8c8'
}

// One stop on the timeline rail: colored dot + connector line, content on the right.
const TimelineEntry = ({
    dotColor,
    isLast,
    children,
}: {
    dotColor?: string
    isLast: boolean
    children: React.ReactNode
}) => (
    <div className={clsx('relative pl-5', isLast ? 'pb-[2px]' : 'pb-3.5')}>
        {!isLast && (
            <div className="absolute bottom-[-3px] left-[4px] top-[15px] w-[2px] bg-[#ececec]" />
        )}
        <span
            className="absolute left-0 top-1 h-2.5 w-2.5 rounded-full shadow-[0_0_0_3px_#ffffff]"
            style={{ backgroundColor: dotColor }}
        />
        {children}
    </div>
)

// One row of the structured trail: status pills for transitions, a labeled pill for
// marked_lost / debt_collection events, with actor + timestamp underneath.
const InvoiceLogEntry = ({ log }: { log: InvoiceLog }) => {
    const content = log.content || {}
    const isManual = Boolean(log.user_id)
    const body = (() => {
        if (log.event_name === INVOICE_LOG_EVENTS.statusUpdate) {
            return (
                <span className="inline-flex items-center gap-1.5">
                    <StatusPill status={content.from_status} muted />
                    <span className="text-sm text-[#c0c0c0]">→</span>
                    <StatusPill status={content.to_status} />
                </span>
            )
        }
        if (log.event_name === INVOICE_LOG_EVENTS.markedLost) {
            return content.undo ? (
                <StatusPill status="lost undone" muted />
            ) : (
                <StatusPill status={INVOICE_LOG_EVENTS.markedLost} />
            )
        }
        if (log.event_name === INVOICE_LOG_EVENTS.debtCollection) {
            return content.undo ? (
                <StatusPill status="removed from debt collection" muted />
            ) : (
                <StatusPill status={INVOICE_LOG_EVENTS.debtCollection} />
            )
        }
        if (log.event_name === INVOICE_LOG_EVENTS.partialPayment) {
            return (
                <span className="inline-flex items-center gap-1.5">
                    <StatusPill
                        status={`partial payment ${formatEuro(content.amount)}`}
                        colors={partialPaymentColors}
                    />
                    {content.open_amount !== undefined && (
                        <span className="text-[11px] text-[#909090]">
                            {formatEuro(content.open_amount)} open
                        </span>
                    )}
                </span>
            )
        }
        if (log.event_name === INVOICE_LOG_EVENTS.molliePaymentEvent) {
            const label = (content.kind && molliePaymentEventLabels[content.kind]) || content.kind
            return (
                <span className="inline-flex items-center gap-1.5">
                    <StatusPill
                        status={`${label} ${formatEuro(content.amount)}`}
                        colors={molliePaymentEventColors}
                    />
                    <span className="text-[11px] text-[#909090]">
                        {content.bank_reason_code && `${content.bank_reason_code} · `}kept fulfilled
                    </span>
                </span>
            )
        }
        return <StatusPill status={log.event_name} />
    })()

    return (
        <>
            {body}
            <div className="mt-[3px] flex items-center gap-1 text-[11px] text-[#909090]">
                <Icon
                    name={isManual ? 'person' : 'smart_toy'}
                    fontSize={13}
                    style={{ color: '#b5b5b5' }}
                />
                <span className={isManual ? 'font-semibold' : 'font-normal'}>
                    {invoiceLogActor(log)}
                </span>
                <span className="text-[#c8c8c8]">·</span>
                {log.created && format(new Date(log.created), 'd MMM yyyy, HH:mm')}
            </div>
        </>
    )
}

// History hover in the status cell: structured trail (invoices_log) rendered as a
// timeline, legacy Mollie log messages in a visually quieter section below it.
const InvoiceStatusHistory = ({ invoice }: { invoice: InvoiceWithLogs }) => {
    const structuredLogs = invoice.invoices_logs || []
    // Newest first, matching the structured trail (the backend returns these in insertion order).
    const mollieLogs = [...(invoice.mollie_logs || [])].sort((a, b) => b.log_id - a.log_id)
    if (structuredLogs.length === 0 && mollieLogs.length === 0) {
        return null
    }

    return (
        <HoverText
            whiteBg
            trigger={<Icon name="history" />}
            bubbleStyle={{ maxHeight: 360, overflowY: 'auto', padding: '14px 16px' }}>
            <div className="mb-3 flex items-baseline gap-1.5 border-b border-[#ececec] pb-2.5">
                <span className="text-[13px] font-bold text-[#333333]">Status history</span>
                <span className="text-[11px] text-[#a0a0a0]">{invoice.invoice_nr}</span>
            </div>
            {structuredLogs.length > 0 && (
                <div className={mollieLogs.length > 0 ? 'mb-3.5' : ''}>
                    {structuredLogs.map((log, index) => (
                        <TimelineEntry
                            key={log.invoices_log_id}
                            dotColor={invoiceLogDotColor(log)}
                            isLast={index === structuredLogs.length - 1}>
                            <InvoiceLogEntry log={log} />
                        </TimelineEntry>
                    ))}
                </div>
            )}
            {mollieLogs.length > 0 && (
                <div className="rounded-md border border-[#efefef] bg-[#fafafa] px-2.5 py-2">
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[#a0a0a0]">
                        Mollie logs
                    </div>
                    {mollieLogs.map((log) => (
                        <div key={log.log_id} className="py-[3px]">
                            <div className="text-sm leading-4 text-[#606060]">{log.message}</div>
                            {log.created && (
                                <div className="mt-px text-[10px] text-[#a8a8a8]">
                                    {format(new Date(log.created), 'd MMM yyyy, HH:mm')}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </HoverText>
    )
}

export default InvoiceStatusHistory
